import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  Box,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  Remove as RemoveIcon,
  Call as CallIcon,
  Videocam as VideocamIcon,
  OpenWith as DragIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
} from '@mui/icons-material';

import { useCallOverlay } from '../../contexts/CallOverlayContext';
import CallScreen from '../../pages/CallScreen';

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

const FloatingCallOverlay = () => {
  const { isOpen, isMinimized, callParams, closeCall, minimize, expand } = useCallOverlay();

  // Size modes
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Window dimensions - normal and expanded
  const normalWidth = 400;
  const normalHeight = 600;
  const expandedWidth = Math.min(900, window.innerWidth - 40);
  const expandedHeight = Math.min(700, window.innerHeight - 40);
  
  const windowWidth = isExpanded ? expandedWidth : normalWidth;
  const windowHeight = isExpanded ? expandedHeight : normalHeight;
  const headerHeight = 48;
  const bubbleSize = 64;
  const minWidth = 320;
  const minHeight = 400;
  
  // Custom size state for resizing
  const [customSize, setCustomSize] = useState({ width: normalWidth, height: normalHeight });
  
  // Use custom size when not in preset expanded mode
  const currentWidth = isExpanded ? expandedWidth : customSize.width;
  const currentHeight = isExpanded ? expandedHeight : customSize.height;

  // Position state - center the window initially
  const [pos, setPos] = useState({ 
    x: Math.max(20, window.innerWidth / 2 - normalWidth / 2), 
    y: Math.max(20, window.innerHeight / 2 - normalHeight / 2) 
  });
  const draggingRef = useRef(false);
  const resizingRef = useRef(false);
  const startRef = useRef({ x: 0, y: 0, mx: 0, my: 0, w: 0, h: 0 });

  // Update position when window resizes (keep window in bounds)
  useEffect(() => {
    const onResize = () => {
      setPos((prev) => ({
        x: clamp(prev.x, 8, window.innerWidth - (isMinimized ? bubbleSize : currentWidth) - 8),
        y: clamp(prev.y, 8, window.innerHeight - (isMinimized ? bubbleSize : currentHeight) - 8),
      }));
      // Reset expanded size on window resize
      if (isExpanded) {
        setCustomSize({
          width: Math.min(900, window.innerWidth - 40),
          height: Math.min(700, window.innerHeight - 40),
        });
      }
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isMinimized, isExpanded, currentWidth, currentHeight]);

  // Drag handlers for the window header
  const onHeaderMouseDown = useCallback((e) => {
    if (isExpanded) return; // Disable dragging when expanded
    e.preventDefault();
    draggingRef.current = true;
    startRef.current = { x: pos.x, y: pos.y, mx: e.clientX, my: e.clientY, w: currentWidth, h: currentHeight };

    const onMove = (ev) => {
      if (!draggingRef.current) return;
      const dx = ev.clientX - startRef.current.mx;
      const dy = ev.clientY - startRef.current.my;
      setPos({
        x: clamp(startRef.current.x + dx, 8, window.innerWidth - currentWidth - 8),
        y: clamp(startRef.current.y + dy, 8, window.innerHeight - currentHeight - 8),
      });
    };

    const onUp = () => {
      draggingRef.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [pos.x, pos.y, currentWidth, currentHeight, isExpanded]);

  // Drag handlers for the minimized bubble
  const onBubbleMouseDown = useCallback((e) => {
    e.preventDefault();
    draggingRef.current = true;
    startRef.current = { x: pos.x, y: pos.y, mx: e.clientX, my: e.clientY, w: 0, h: 0 };

    const onMove = (ev) => {
      if (!draggingRef.current) return;
      const dx = ev.clientX - startRef.current.mx;
      const dy = ev.clientY - startRef.current.my;
      setPos({
        x: clamp(startRef.current.x + dx, 8, window.innerWidth - bubbleSize - 8),
        y: clamp(startRef.current.y + dy, 8, window.innerHeight - bubbleSize - 8),
      });
    };

    const onUp = () => {
      draggingRef.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [pos.x, pos.y]);

  // Resize handlers
  const onResizeMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    resizingRef.current = true;
    startRef.current = { x: pos.x, y: pos.y, mx: e.clientX, my: e.clientY, w: customSize.width, h: customSize.height };

    const onMove = (ev) => {
      if (!resizingRef.current) return;
      const dx = ev.clientX - startRef.current.mx;
      const dy = ev.clientY - startRef.current.my;
      const newWidth = clamp(startRef.current.w + dx, minWidth, window.innerWidth - pos.x - 20);
      const newHeight = clamp(startRef.current.h + dy, minHeight, window.innerHeight - pos.y - 20);
      setCustomSize({ width: newWidth, height: newHeight });
      if (isExpanded) setIsExpanded(false);
    };

    const onUp = () => {
      resizingRef.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [customSize.width, customSize.height, pos.x, pos.y, isExpanded]);

  const toggleExpand = () => {
    setIsExpanded((prev) => !prev);
    if (!isExpanded) {
      // Center the expanded window
      setPos({
        x: Math.max(20, window.innerWidth / 2 - expandedWidth / 2),
        y: Math.max(20, window.innerHeight / 2 - expandedHeight / 2),
      });
    } else {
      // Return to custom size and center
      setPos({
        x: Math.max(20, window.innerWidth / 2 - customSize.width / 2),
        y: Math.max(20, window.innerHeight / 2 - customSize.height / 2),
      });
    }
  };

  if (!isOpen || !callParams) return null;

  const isVideo = callParams.callType === 'video';

  return (
    <>
      {/* Floating Window */}
      {!isMinimized && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            left: pos.x,
            top: pos.y,
            width: currentWidth,
            height: currentHeight,
            borderRadius: 2,
            overflow: 'hidden',
            zIndex: 2000,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#fff',
          }}
        >
          {/* Draggable Header */}
          <Box
            sx={{
              height: headerHeight,
              px: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              cursor: isExpanded ? 'default' : 'move',
              userSelect: 'none',
            }}
            onMouseDown={onHeaderMouseDown}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {!isExpanded && <DragIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
              <Typography fontWeight={600}>
                {isVideo ? 'Video Call' : 'Voice Call'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton onClick={toggleExpand} size="small" title={isExpanded ? 'Shrink' : 'Expand'}>
                {isExpanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
              <IconButton onClick={minimize} size="small">
                <RemoveIcon />
              </IconButton>
              <IconButton onClick={closeCall} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Call Content */}
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <CallScreen overlayParams={callParams} onRequestClose={closeCall} />
          </Box>

          {/* Resize Handle */}
          {!isExpanded && (
            <Box
              sx={{
                position: 'absolute',
                right: 0,
                bottom: 0,
                width: 20,
                height: 20,
                cursor: 'se-resize',
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'flex-end',
                p: 0.5,
                '&::after': {
                  content: '""',
                  width: 8,
                  height: 8,
                  borderRight: '2px solid',
                  borderBottom: '2px solid',
                  borderColor: 'text.disabled',
                  borderBottomRightRadius: 2,
                },
                '&:hover::after': {
                  borderColor: 'primary.main',
                },
              }}
              onMouseDown={onResizeMouseDown}
            />
          )}
        </Paper>
      )}

      {/* Minimized Bubble */}
      {isMinimized && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            left: pos.x,
            top: pos.y,
            width: bubbleSize,
            height: bubbleSize,
            borderRadius: '50%',
            overflow: 'hidden',
            zIndex: 2000,
            cursor: 'grab',
            userSelect: 'none',
          }}
          onMouseDown={onBubbleMouseDown}
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              bgcolor: 'primary.main',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
            }}
            onClick={() => {
              if (draggingRef.current) return;
              expand();
            }}
          >
            {isVideo ? <VideocamIcon /> : <CallIcon />}
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                closeCall();
              }}
              sx={{
                position: 'absolute',
                top: -6,
                right: -6,
                bgcolor: 'rgba(0,0,0,0.6)',
                color: '#fff',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
              }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        </Paper>
      )}
    </>
  );
};

export default FloatingCallOverlay;
